import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../services/api';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

interface VendorData {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  tax_number: string;
  vat_number: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  default_currency: string;
  payment_terms: string;
  bank_name: string;
  bank_account: string;
  bank_branch: string;
  notes: string;
  is_active: boolean;
}

const initialData: VendorData = {
  name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'South Africa',
  tax_number: '',
  vat_number: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  default_currency: 'ZAR',
  payment_terms: '',
  bank_name: '',
  bank_account: '',
  bank_branch: '',
  notes: '',
  is_active: true,
};

export default function VendorForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState<VendorData>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing) {
      fetchVendor();
    }
  }, [id]);

  const fetchVendor = async () => {
    setIsLoading(true);
    try {
      const data = await apiGet<VendorData & { id: string }>(`/api/vendors/${id}`);
      setFormData({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        postal_code: data.postal_code || '',
        country: data.country || 'South Africa',
        tax_number: data.tax_number || '',
        vat_number: data.vat_number || '',
        contact_name: data.contact_name || '',
        contact_email: data.contact_email || '',
        contact_phone: data.contact_phone || '',
        default_currency: data.default_currency || 'ZAR',
        payment_terms: data.payment_terms || '',
        bank_name: data.bank_name || '',
        bank_account: data.bank_account || '',
        bank_branch: data.bank_branch || '',
        notes: data.notes || '',
        is_active: data.is_active ?? true,
      });
    } catch (err) {
      setError('Failed to load vendor');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        email: formData.email || null,
        phone: formData.phone || null,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        postal_code: formData.postal_code || null,
        tax_number: formData.tax_number || null,
        vat_number: formData.vat_number || null,
        contact_name: formData.contact_name || null,
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null,
        payment_terms: formData.payment_terms || null,
        bank_name: formData.bank_name || null,
        bank_account: formData.bank_account || null,
        bank_branch: formData.bank_branch || null,
        notes: formData.notes || null,
      };

      if (isEditing) {
        await apiPut(`/api/vendors/${id}`, payload);
      } else {
        await apiPost('/api/vendors', payload);
      }
      navigate('/admin/vendors');
    } catch (err: any) {
      setError(err.message || 'Failed to save vendor');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/vendors')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Vendors
        </button>
      </div>

      <div className="bg-dark-900 rounded-xl border border-dark-800 p-6">
        <h1 className="text-2xl font-bold text-white mb-6">
          {isEditing ? 'Edit Vendor' : 'New Vendor'}
        </h1>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">Vendor Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Phone</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-white mb-3">Address</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <input type="text" name="address" value={formData.address} onChange={handleChange} placeholder="Street Address" className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="City" className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="text" name="state" value={formData.state} onChange={handleChange} placeholder="State/Province" className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} placeholder="Postal Code" className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="text" name="country" value={formData.country} onChange={handleChange} placeholder="Country" className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-white mb-3">Banking Details</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <input type="text" name="bank_name" value={formData.bank_name} onChange={handleChange} placeholder="Bank Name" className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="text" name="bank_account" value={formData.bank_account} onChange={handleChange} placeholder="Account Number" className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="text" name="bank_branch" value={formData.bank_branch} onChange={handleChange} placeholder="Branch Code" className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" name="is_active" id="is_active" checked={formData.is_active} onChange={handleChange} className="w-4 h-4 rounded border-dark-700 bg-dark-800 text-primary-500" />
            <label htmlFor="is_active" className="text-sm text-gray-300">Active vendor</label>
          </div>

          <div className="flex gap-4 pt-4">
            <button type="submit" disabled={isSaving} className="flex items-center gap-2 px-6 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors">
              {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {isSaving ? 'Saving...' : 'Save Vendor'}
            </button>
            <button type="button" onClick={() => navigate('/admin/vendors')} className="px-6 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
